import { Howl, Howler } from 'howler';
import { eventBus } from '../events/EventBus';

// ============================================
// AudioManager - Gestor de audio del juego
// ============================================

export class AudioManager {
  private sounds: Map<string, Howl> = new Map();
  private masterVolume = 0.7;

  private initialized = false;

  init(): void {
    Howler.volume(this.masterVolume);
    this.initialized = true;

    // Pre-cargar sonidos esenciales
    this.loadSound('radio-static', '/audio/radio-static.mp3', {
      volume: 0.5,
      loop: true,
    });

    this.loadSound('ambient-room', '/audio/room-tone.mp3', {
      volume: 0.3,
      loop: true,
    });

    this.loadSound('footstep', '/audio/footstep.mp3', {
      volume: 0.4,
      loop: false,
    });

    this.loadSound('door-creak', '/audio/door-creak.mp3', {
      volume: 0.6,
      loop: false,
    });

    this.loadSound('whisper', '/audio/whisper.mp3', {
      volume: 0.5,
      loop: false,
    });

    // Eventos
    eventBus.on('audio:play', (data) => {
      this.play(data.id);
    });

    eventBus.on('audio:stop', (id) => {
      this.stop(id);
    });

    eventBus.on('audio:volumeChange', (data) => {
      this.setVolume(data.id, data.volume);
    });

    console.log('[AudioManager] Inicializado');
  }

  loadSound(
    id: string,
    url: string,
    options: {
      volume?: number;
      loop?: boolean;
    } = {}
  ): void {
    if (this.sounds.has(id)) return;

    const sound = new Howl({
      src: [url],
      volume: options.volume ?? 0.5,
      loop: options.loop ?? false,
      preload: true,
      html5: options.loop, // Usar HTML5 audio para sonidos largos en loop
    });

    this.sounds.set(id, sound);
  }

  play(id: string): void {
    const sound = this.sounds.get(id);
    if (sound) {
      sound.play();
    } else {
      console.warn(`[AudioManager] Sonido no encontrado: ${id}`);
    }
  }

  stop(id: string): void {
    const sound = this.sounds.get(id);
    if (sound) {
      sound.stop();
    }
  }

  pause(id: string): void {
    const sound = this.sounds.get(id);
    if (sound) {
      sound.pause();
    }
  }

  setVolume(id: string, volume: number): void {
    const sound = this.sounds.get(id);
    if (sound) {
      sound.volume(volume);
    }
  }

  setMasterVolume(volume: number): void {
    this.masterVolume = Math.max(0, Math.min(1, volume));
    Howler.volume(this.masterVolume);
  }

  setCategoryVolume(_category: 'ambient' | 'sfx' | 'music' | 'voice', volume: number): void {
    const clampedVolume = Math.max(0, Math.min(1, volume));

    this.sounds.forEach((sound) => {
      // Aquí se podría filtrar por categoría si se almacena
      sound.volume(clampedVolume);
    });
  }

  update(_deltaTime: number): void {
    // Actualizaciones futuras: fading espacial, etc.
  }

  dispose(): void {
    this.sounds.forEach((sound) => {
      sound.unload();
    });
    this.sounds.clear();
    this.initialized = false;
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  getLoadedSounds(): string[] {
    return Array.from(this.sounds.keys());
  }
}
